import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Divider,
    IconButton,
    InputAdornment,
    Chip,
    MenuItem,
} from '@mui/material';
import {
    Check as CheckIcon,
    Mic as MicIcon,
    MicOff as MicOffIcon,
    Search as SearchIcon,
    FiberManualRecord as DotIcon,
    Add as AddIcon,
} from '@mui/icons-material';
import timesheetService from '../../../services/timesheetService';
import { enqueueJobCodeOp, getPendingJobCodeCreates, getJobCodeCatalog, EVT_JOB_CODES_CHANGED, EVT_JOBS_CHANGED } from '../../../services/timesheetSyncService';
import useDictation from '../../../hooks/useDictation';
import { ATTEND_CODES } from './CellEditModal';
import AttendDaysPicker from './AttendDaysPicker';

const ATTEND_SHORT = { 'No Call No Show': 'NC', 'Excused': 'EX', 'Unexcused': 'UN', 'Termed - ADP entered': 'TM', 'Night work': 'NW' };
const NIGHT_CODE = 'Night work';

function formatDateLong(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];
    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

export default function EmployeeNotesModal({
    open,
    onClose,
    employeeName,
    employeeId,
    jobs,
    allJobs,
    onAddJobs,
    records,
    weekRecords,
    dailyNotes,
    currentDate,
    onSaveNote,
    onSaveGeneralNote,
    onSetAttendCode,
}) {
    const [selectedJobId, setSelectedJobId] = useState(null);
    // Дни недели, к которым применить attend-код выбранной работы (мультивыбор).
    const [applyDates, setApplyDates] = useState([]);
    // Код, которым управляет пикер дней: код текущего дня, а если его сняли/нет —
    // тот, что уже стоит на неделе по этой работе. Иначе, сняв код с текущего дня,
    // мы бы погасили чипы остальных дней, где он никуда не делся.
    const [pickerCode, setPickerCode] = useState('');
    const weekDates = useMemo(
        () => (currentDate ? timesheetService.getWeekDates(currentDate) : []),
        [currentDate],
    );
    const [noteTexts, setNoteTexts] = useState({});
    const [generalNote, setGeneralNote] = useState('');
    const [savedJobs, setSavedJobs] = useState(new Set());
    const [generalNoteSaved, setGeneralNoteSaved] = useState(false);
    const [search, setSearch] = useState('');
    const saveTimers = useRef({});
    const generalNoteTimer = useRef(null);

    // Новый код работы (7888-1) заводится только отсюда — из заметок сотрудника.
    const [pendingCodes, setPendingCodes] = useState(() => getPendingJobCodeCreates());
    const [jobCodeCatalog, setJobCodeCatalog] = useState(() => getJobCodeCatalog());
    const [addCode, setAddCode] = useState({ open: false, desc: '' });
    useEffect(() => {
        const refresh = () => { setPendingCodes(getPendingJobCodeCreates()); setJobCodeCatalog(getJobCodeCatalog()); };
        if (open) refresh();
        window.addEventListener(EVT_JOB_CODES_CHANGED, refresh);
        // Заведённый отсюда код пришёл с сервера — сразу открываем его в заметках.
        const onJobs = (e) => {
            const ids = (e.detail && e.detail.addToGrid) || [];
            if (open && ids.length) wantSelectRef.current = ids[ids.length - 1];
        };
        window.addEventListener(EVT_JOBS_CHANGED, onJobs);
        return () => {
            window.removeEventListener(EVT_JOB_CODES_CHANGED, refresh);
            window.removeEventListener(EVT_JOBS_CHANGED, onJobs);
        };
    }, [open]);
    // Код, выбранный/заведённый здесь, появляется в списке после того, как встал
    // в таймшит, — тогда и переключаемся на него.
    const wantSelectRef = useRef(null);

    // Dictation
    const { isSupported: dictationSupported, isListening, start: startDictation, stop: stopDictation } = useDictation({
        lang: 'en-US', interimResults: false, continuous: false, safetyTimeoutMs: 15000,
    });
    const dictationTargetRef = useRef(null);
    const [dictationTarget, setDictationTarget] = useState(null);

    const toggleDictation = useCallback(async (target) => {
        if (!dictationSupported) return;
        if (isListening && dictationTarget === target) {
            stopDictation();
            setDictationTarget(null);
            dictationTargetRef.current = null;
            return;
        }
        dictationTargetRef.current = target;
        setDictationTarget(target);
        await startDictation({
            onResult: (transcript) => {
                if (!transcript || !dictationTargetRef.current) return;
                const t = dictationTargetRef.current;
                if (t === 'general') {
                    setGeneralNote(prev => {
                        const next = ((prev || '') + (prev ? ' ' : '') + transcript).trim();
                        if (generalNoteTimer.current) clearTimeout(generalNoteTimer.current);
                        generalNoteTimer.current = setTimeout(() => {
                            if (onSaveGeneralNote) {
                                onSaveGeneralNote(employeeId, next);
                                setGeneralNoteSaved(true);
                            }
                        }, 500);
                        return next;
                    });
                } else {
                    setNoteTexts(prev => {
                        const next = ((prev[t] || '') + (prev[t] ? ' ' : '') + transcript).trim();
                        if (saveTimers.current[t]) clearTimeout(saveTimers.current[t]);
                        saveTimers.current[t] = setTimeout(() => {
                            onSaveNote(t, next);
                            setSavedJobs(s => new Set(s).add(t));
                        }, 500);
                        return { ...prev, [t]: next };
                    });
                }
            },
            onEnd: () => { setDictationTarget(null); dictationTargetRef.current = null; },
        });
    }, [dictationSupported, isListening, dictationTarget, startDictation, stopDictation, employeeId, onSaveGeneralNote, onSaveNote]);

    // ВСЕ работы дня (не только с часами): absence-код ставится именно там, где
    // человек НЕ работал — такие работы обязаны быть в списке (создаётся 0ч-запись).
    const indexedJobs = useMemo(() => {
        const byJobId = new Map();
        for (const r of (records || [])) {
            if (r.employeeId === employeeId && !r.isDeleted) byJobId.set(r.jobId, r);
        }
        return jobs
            .map(j => {
                const rec = byJobId.get(j.id);
                const name = timesheetService.getJobName(j) || '';
                const code = timesheetService.getJobCode(j) || '';
                return {
                    ...j,
                    _name: name,
                    _code: code,
                    _nameLc: `${name} ${code}`.toLowerCase(),
                    _hours: rec?.hours || 0,
                    _attendCode: rec?.attendCode || '',
                    _initialNote: rec?.notes || '',
                };
            })
            .sort((a, b) => a._name.localeCompare(b._name));
    }, [jobs, records, employeeId]);

    const filteredJobs = useMemo(() => {
        const term = search.trim().toLowerCase();
        const matched = term ? indexedJobs.filter(j => j._nameLc.includes(term)) : indexedJobs;
        // Сначала работы, где сегодня что-то есть (часы/код/заметка), затем остальные.
        const withData = matched.filter(j => j._hours > 0 || j._attendCode || j._initialNote);
        const withoutData = matched.filter(j => !(j._hours > 0 || j._attendCode || j._initialNote));
        return { withData, withoutData };
    }, [indexedJobs, search]);

    // One-time init guard — prevents wiping user's in-progress typing when
    // records/dailyNotes update as a side effect of our own saves.
    const initDoneRef = useRef(false);
    useEffect(() => {
        if (!open) {
            initDoneRef.current = false;
            return;
        }
        if (!employeeId || initDoneRef.current) return;

        const texts = {};
        for (const job of indexedJobs) {
            texts[job.id] = job._initialNote;
        }
        setNoteTexts(texts);

        const genNote = (dailyNotes || []).find(n =>
            n.employeeId === employeeId && n.noteDate === currentDate && !n.jobId
        );
        setGeneralNote(genNote?.notes || '');

        setSavedJobs(new Set());
        setGeneralNoteSaved(false);
        const firstWithData = indexedJobs.find(j => j._hours > 0 || j._attendCode || j._initialNote);
        setSelectedJobId((firstWithData || indexedJobs[0])?.id || null);
        setSearch('');
        initDoneRef.current = true;
    }, [open, employeeId, indexedJobs, dailyNotes, currentDate]);

    /** Карта {дата: код} по выбранной работе за неделю (свои записи фильтрует родитель). */
    const weekAttend = useMemo(() => {
        const map = {};
        for (const r of (weekRecords || [])) {
            if (r.employeeId === employeeId && r.jobId === selectedJobId && !r.isDeleted && r.attendCode) {
                map[r.workDate] = r.attendCode;
            }
        }
        return map;
    }, [weekRecords, employeeId, selectedJobId]);

    const daysWithCode = useCallback(
        (code) => (code ? Object.keys(weekAttend).filter(d => weekAttend[d] === code) : []),
        [weekAttend],
    );

    // Смена работы/кода — набор дней показывает, где этот код уже стоит (+ текущий день).
    // weekAttend читаем через ref: иначе фоновый sync (обновляет weekRecords каждые
    // ~15 сек) пересоздавал бы daysWithCode и сбрасывал отметки прямо под руками.
    const selectedJobCode = indexedJobs.find(j => j.id === selectedJobId)?._attendCode || '';
    const weekAttendRef = useRef(weekAttend);
    weekAttendRef.current = weekAttend;
    useEffect(() => {
        const map = weekAttendRef.current || {};
        const dates = Object.keys(map).sort();
        // Отмечены ровно те дни, где код стоит. Текущий день попадает сам, если код
        // на нём есть; принудительно его не отмечаем — пустой день не должен гореть.
        const code = selectedJobCode || (dates.length ? map[dates[0]] : '');
        setPickerCode(code);
        setApplyDates(code ? dates.filter(d => map[d] === code) : []);
    }, [selectedJobId, selectedJobCode, currentDate]);

    /**
     * Смена кода: применяем к текущему дню + ко всем отмеченным дням. Так работает
     * любой порядок действий — можно сперва отметить дни, потом выбрать код.
     * Дни прежнего кода, которые не отмечены, — очищаем.
     *
     * Сброс в None снимает код ТОЛЬКО с текущего дня: раньше пустой код уезжал на
     * все отмеченные дни и стирал ночь со всей недели разом.
     */
    const handleAttendChange = useCallback((jobId, code) => {
        const dayCode = code || pickerCode || '';
        const apply = code ? applyDates.filter(d => d !== currentDate) : [];
        const remove = (selectedJobCode && selectedJobCode !== code)
            ? daysWithCode(selectedJobCode).filter(d => d !== currentDate && !applyDates.includes(d))
            : [];
        const dayOps = (apply.length || remove.length) ? { code: dayCode, apply, remove } : null;
        onSetAttendCode?.(jobId, code, dayOps);
    }, [applyDates, currentDate, selectedJobCode, pickerCode, daysWithCode, onSetAttendCode]);

    /**
     * Смена набора дней. Семантика как в редакторе ячейки: отмеченные = где код стоит.
     * Ставим код на добавленные дни, снимаем с тех, где галочку убрали. Текущий день —
     * обычный член набора: сняли его чип → сняли код с этого дня (без него отменить
     * день, стоя на нём, было нельзя вообще).
     */
    const handleDaysChange = useCallback((jobId, code, dates) => {
        setApplyDates(dates);
        if (!code) return;
        const apply = dates.filter(d => d !== currentDate);
        const remove = daysWithCode(code).filter(d => d !== currentDate && !dates.includes(d));
        const dayOps = (apply.length || remove.length) ? { code, apply, remove } : null;
        const nextCurrent = dates.includes(currentDate) ? code : '';
        const currentChanged = nextCurrent !== (selectedJobCode || '');
        if (!dayOps && !currentChanged) return;
        onSetAttendCode?.(jobId, currentChanged ? nextCurrent : (selectedJobCode || ''), dayOps);
    }, [currentDate, selectedJobCode, daysWithCode, onSetAttendCode]);

    const handleJobNoteChange = useCallback((jobId, text) => {
        setNoteTexts(prev => ({ ...prev, [jobId]: text }));
        if (saveTimers.current[jobId]) clearTimeout(saveTimers.current[jobId]);
        saveTimers.current[jobId] = setTimeout(() => {
            onSaveNote(jobId, text);
            setSavedJobs(prev => new Set(prev).add(jobId));
        }, 500);
    }, [onSaveNote]);

    const handleGeneralNoteChange = useCallback((text) => {
        setGeneralNote(text);
        if (generalNoteTimer.current) clearTimeout(generalNoteTimer.current);
        generalNoteTimer.current = setTimeout(() => {
            if (onSaveGeneralNote) {
                onSaveGeneralNote(employeeId, text);
                setGeneralNoteSaved(true);
            }
        }, 500);
    }, [employeeId, onSaveGeneralNote]);

    const handleClose = () => {
        Object.values(saveTimers.current).forEach(clearTimeout);
        if (generalNoteTimer.current) clearTimeout(generalNoteTimer.current);
        if (selectedJobId && noteTexts[selectedJobId] !== undefined) {
            onSaveNote(selectedJobId, noteTexts[selectedJobId]);
        }
        if (onSaveGeneralNote) {
            onSaveGeneralNote(employeeId, generalNote);
        }
        onClose();
    };

    useEffect(() => {
        return () => {
            Object.values(saveTimers.current).forEach(clearTimeout);
            if (generalNoteTimer.current) clearTimeout(generalNoteTimer.current);
        };
    }, []);

    const notesCount = Object.values(noteTexts).filter(t => t && t.trim()).length;
    const selectedJob = indexedJobs.find(j => j.id === selectedJobId);

    // Код всегда заводится на основную работу: у кода 7888-1 — на 7888.
    const parentCode = selectedJob ? String(selectedJob.parentJob || selectedJob._code || '') : '';
    const parentId = selectedJob ? (selectedJob.parentJobId || selectedJob.id) : null;
    const pendingForJob = pendingCodes.filter(p => String(p.job) === parentCode);
    const pendingNumbered = pendingForJob.filter(p => !p.code);
    // Уже существующие коды этой работы (7888-1, 7888-2…) — их можно выбрать.
    const existingCodes = useMemo(() => (allJobs || [])
        .filter(j => parentId && j.parentJobId === parentId)
        .sort((a, b) => (Number(a.jobCodeNo) || 0) - (Number(b.jobCodeNo) || 0)),
    [allJobs, parentId]);
    // Остальные коды из внешней JobCode: этой работы (заведены другим форманом,
    // в админке, в Document Manager — строки у нас ещё нет) и общие (без работы:
    // RT 452, ST 794…). Выбранный становится строкой работы (7888-RT 452), как 7888-1.
    const globalCodes = useMemo(() => {
        const have = new Set(existingCodes.map(j => String(j.jobCodeNo)));
        const seen = new Set();
        return jobCodeCatalog
            .map(c => ({ ...c, code: String(c.code || '').trim(), own: String(c.job || '').trim() === parentCode }))
            .filter(c => {
                if (!c.code || (!c.own && String(c.job || '').trim() !== '') || have.has(c.code) || seen.has(c.code)) return false;
                seen.add(c.code);
                return true;
            })
            .sort((a, b) => (b.own - a.own) || a.code.localeCompare(b.code, undefined, { numeric: true }));
    }, [jobCodeCatalog, existingCodes, parentCode]);
    useEffect(() => {
        const want = wantSelectRef.current;
        if (want && indexedJobs.some(j => j.id === want)) {
            wantSelectRef.current = null;
            setSelectedJobId(want);
        }
    }, [indexedJobs]);
    // Выбор кода: если его ещё нет в таймшите — ставим строкой, и переходим к нему.
    const handlePickCode = (value) => {
        if (String(value).startsWith('g:')) {
            const code = String(value).slice(2);
            if (!pendingForJob.some(p => p.code === code)) {
                const g = globalCodes.find(c => c.code === code);
                enqueueJobCodeOp({ op: 'create', jobId: parentId, job: parentCode, code, description: g?.description || null, addToGrid: true });
                setPendingCodes(getPendingJobCodeCreates());
            }
            return;
        }
        const jobId = Number(value);
        if (!jobId || jobId === selectedJobId) return;
        if (indexedJobs.some(j => j.id === jobId)) { setSelectedJobId(jobId); return; }
        wantSelectRef.current = jobId;
        onAddJobs?.([jobId]);
    };
    // Следующий номер — как на сервере: максимальный числовой код работы + 1
    // (плюс ещё не отправленные). Показываем его до добавления.
    const lastCodeNo = useMemo(() => {
        let max = 0;
        const take = (v) => { if (/^\d+$/.test(String(v ?? ''))) max = Math.max(max, Number(v)); };
        for (const c of jobCodeCatalog) if (String(c.job) === parentCode) take(c.code);
        for (const j of existingCodes) take(j.jobCodeNo);
        return max;
    }, [jobCodeCatalog, existingCodes, parentCode]);
    const nextCode = `#${parentCode}-${lastCodeNo + pendingNumbered.length + 1}`;
    useEffect(() => { setAddCode({ open: false, desc: '' }); }, [selectedJobId]);

    // Номер не вводят — сервер выдаёт следующий по порядку (7888-1, 7888-2…).
    // Когда код придёт, он сам встаёт в таймшит строкой (addToGrid).
    const handleAddCode = () => {
        const desc = addCode.desc.trim();
        if (!desc || !parentId || !parentCode) return;
        enqueueJobCodeOp({ op: 'create', jobId: parentId, job: parentCode, description: desc, addToGrid: true });
        setPendingCodes(getPendingJobCodeCreates());
        setAddCode({ open: false, desc: '' });
    };

    const renderJobRow = (job) => {
        const hasNote = noteTexts[job.id] && noteTexts[job.id].trim();
        const isSaved = savedJobs.has(job.id);
        const isSelected = selectedJobId === job.id;
        return (
            <button
                key={job.id}
                type="button"
                onClick={() => setSelectedJobId(job.id)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    background: isSelected ? '#e3f2fd' : 'transparent',
                    borderLeft: isSelected ? '3px solid #1976d2' : '3px solid transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: 'inherit',
                    WebkitTapHighlightColor: 'transparent',
                }}
            >
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                        fontSize: 13,
                        fontWeight: isSelected ? 600 : 500,
                        color: '#222',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}>
                        {job._code && (
                            <span style={{ color: '#1976d2', fontWeight: 700, marginRight: 4 }}>
                                #{job._code}
                            </span>
                        )}
                        {job._name}
                    </div>
                    {job._hours > 0 && (
                        <div style={{ fontSize: 11, color: '#2e7d32', fontWeight: 500 }}>
                            {job._hours}h
                        </div>
                    )}
                </div>
                {job._attendCode && (
                    <span style={{
                        padding: '1px 4px', borderRadius: 4,
                        background: job._attendCode === NIGHT_CODE ? '#312e81' : '#dc2626', color: '#fff',
                        fontSize: 9, fontWeight: 700, letterSpacing: '0.03em',
                    }}>
                        {ATTEND_SHORT[job._attendCode] || job._attendCode.slice(0, 2).toUpperCase()}
                    </span>
                )}
                {hasNote && <DotIcon sx={{ fontSize: 10, color: 'warning.main' }} />}
                {isSaved && <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} />}
            </button>
        );
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { height: '85vh' } }}>
            <DialogTitle sx={{ pb: 1 }}>
                Notes: {employeeName}
                <Typography variant="caption" display="block" color="text.secondary">
                    {formatDateLong(currentDate)} — {notesCount}/{indexedJobs.length} jobs with notes
                </Typography>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {/* General note — always visible at top */}
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle2">General Note</Typography>
                        {generalNoteSaved && (
                            <Chip icon={<CheckIcon />} label="Saved" size="small" color="success" sx={{ height: 20 }} />
                        )}
                    </Box>
                    <TextField
                        multiline
                        minRows={2}
                        maxRows={4}
                        fullWidth
                        placeholder={dictationSupported ? "Type or dictate general note..." : "General note for this employee today..."}
                        value={generalNote}
                        onChange={(e) => handleGeneralNoteChange(e.target.value)}
                        size="small"
                        InputProps={{
                            endAdornment: dictationSupported && (
                                <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                                    <IconButton
                                        onClick={() => toggleDictation('general')}
                                        color={isListening && dictationTarget === 'general' ? 'error' : 'default'}
                                        size="small"
                                    >
                                        {isListening && dictationTarget === 'general' ? <MicOffIcon /> : <MicIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                {indexedJobs.length === 0 ? (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4, flex: 1 }}>
                        No jobs selected for today
                    </Typography>
                ) : (
                    <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                        {/* Left: jobs list with search */}
                        <Box sx={{
                            width: { xs: '45%', sm: 240 },
                            minWidth: 180,
                            borderRight: '1px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            flexDirection: 'column',
                        }}>
                            <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Search job..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ fontSize: 18 }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                            <Box sx={{ flex: 1, overflow: 'auto', py: 0.5 }}>
                                {filteredJobs.withData.length === 0 && filteredJobs.withoutData.length === 0 ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 2, display: 'block', textAlign: 'center' }}>
                                        No matches
                                    </Typography>
                                ) : (
                                    <>
                                        {filteredJobs.withData.length > 0 && (
                                            <>
                                                <Typography variant="caption" sx={{ px: 1.5, py: 0.5, display: 'block', color: 'success.dark', fontWeight: 600 }}>
                                                    Today ({filteredJobs.withData.length})
                                                </Typography>
                                                {filteredJobs.withData.map(renderJobRow)}
                                            </>
                                        )}
                                        {filteredJobs.withoutData.length > 0 && (
                                            <>
                                                <Divider sx={{ my: 0.5 }}>
                                                    <Typography variant="caption" color="text.disabled">
                                                        No entry ({filteredJobs.withoutData.length})
                                                    </Typography>
                                                </Divider>
                                                {filteredJobs.withoutData.map(renderJobRow)}
                                            </>
                                        )}
                                    </>
                                )}
                            </Box>
                        </Box>

                        {/* Right: note editor for selected job */}
                        <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            {selectedJob ? (
                                <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexShrink: 0 }}>
                                        <Typography variant="subtitle2" sx={{ flex: 1 }}>
                                            {selectedJob._code && (
                                                <span style={{ color: '#1976d2', fontWeight: 700, marginRight: 6 }}>
                                                    #{selectedJob._code}
                                                </span>
                                            )}
                                            {selectedJob._name}
                                        </Typography>
                                        {selectedJob._hours > 0
                                            ? <Chip label={`${selectedJob._hours}h`} size="small" color="success" variant="outlined" />
                                            : <Chip label={selectedJob._attendCode ? 'Absent' : 'No hours'} size="small" color={selectedJob._attendCode ? 'error' : 'default'} variant="outlined" />}
                                        {savedJobs.has(selectedJobId) && (
                                            <Chip icon={<CheckIcon />} label="Saved" size="small" color="success" sx={{ height: 22 }} />
                                        )}
                                    </Box>

                                    {/* Absence code — тот же статус, что в редакторе ячейки (long-press).
                                        Для работы без записи создаётся 0ч-запись (no-show day). */}
                                    <TextField
                                        select
                                        label="Attendance code"
                                        value={selectedJob._attendCode || ''}
                                        onChange={(e) => handleAttendChange(selectedJobId, e.target.value)}
                                        fullWidth
                                        size="small"
                                        sx={{ mb: 1.5, flexShrink: 0 }}
                                        helperText={selectedJob._attendCode === NIGHT_CODE
                                            ? 'Night shift for this worker — pick the days below'
                                            : (selectedJob._attendCode
                                                ? 'Absence day — hours can stay at 0'
                                                : 'Absence codes, or Night work if this worker is on the night shift')}
                                        FormHelperTextProps={{ sx: { color: selectedJob._attendCode === NIGHT_CODE ? '#4338ca' : (selectedJob._attendCode ? '#b91c1c' : undefined) } }}
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {ATTEND_CODES.map(c => (
                                            <MenuItem key={c.value} value={c.value}>
                                                <Box component="span" sx={{
                                                    display: 'inline-block', minWidth: 26, mr: 1, px: 0.5,
                                                    borderRadius: '4px', bgcolor: c.night ? '#312e81' : '#dc2626', color: '#fff',
                                                    fontSize: '0.65rem', fontWeight: 700, textAlign: 'center', lineHeight: '16px',
                                                }}>{c.short}</Box>
                                                {c.night ? '🌙 ' : ''}{c.value}
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    {/* Мультивыбор дней недели: отмеченные = где код стоит;
                                        снятая галочка снимает код с дня. Виден всегда —
                                        дни можно отметить и до выбора кода. */}
                                    {weekDates.length > 0 && (
                                        <Box sx={{ flexShrink: 0 }}>
                                            <AttendDaysPicker
                                                weekDates={weekDates}
                                                value={applyDates}
                                                onChange={(dates) => handleDaysChange(selectedJobId, pickerCode, dates)}
                                                night={pickerCode === NIGHT_CODE}
                                                currentDate={currentDate}
                                                markedDates={Object.keys(weekAttend)}
                                            />
                                            {!pickerCode && (
                                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: -1, mb: 1.5 }}>
                                                    Pick the days, then choose a code above — it applies to all of them.
                                                </Typography>
                                            )}
                                        </Box>
                                    )}

                                    {/* Код работы: у общих работ (Overhead, Training) своих кодов нет. */}
                                    {!selectedJob.isAltJob && (
                                        <Box sx={{ flexShrink: 0, mb: 1.5 }}>
                                            {addCode.open ? (
                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                                    <TextField
                                                        size="small" autoFocus required
                                                        label="Description"
                                                        value={addCode.desc}
                                                        onChange={(e) => setAddCode(s => ({ ...s, desc: e.target.value.slice(0, 255) }))}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') handleAddCode(); }}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <b style={{ color: '#1976d2' }}>{nextCode}</b>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        sx={{ flex: 1 }}
                                                    />
                                                    <Button size="small" variant="contained" disabled={!addCode.desc.trim()} onClick={handleAddCode} sx={{ mt: 0.5 }}>Add</Button>
                                                    <Button size="small" onClick={() => setAddCode({ open: false, desc: '' })} sx={{ mt: 0.5 }}>Cancel</Button>
                                                </Box>
                                            ) : (
                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                    {(existingCodes.length > 0 || globalCodes.length > 0) && (
                                                        <TextField
                                                            select
                                                            size="small"
                                                            label="Job code"
                                                            value={selectedJob.parentJobId ? selectedJob.id : parentId}
                                                            onChange={(e) => handlePickCode(e.target.value)}
                                                            sx={{ flex: 1 }}
                                                        >
                                                            <MenuItem value={parentId}><em>#{parentCode} — no code</em></MenuItem>
                                                            {existingCodes.map(c => (
                                                                <MenuItem key={c.id} value={c.id}>
                                                                    <b style={{ color: '#1976d2', marginRight: 6 }}>#{parentCode}-{c.jobCodeNo}</b>
                                                                    {c.codeDescription || ''}
                                                                </MenuItem>
                                                            ))}
                                                            {globalCodes.map(c => (
                                                                <MenuItem key={'g:' + c.code} value={'g:' + c.code}>
                                                                    <b style={{ color: '#1976d2', marginRight: 6 }}>{c.own ? `#${parentCode}-${c.code}` : c.code}</b>
                                                                    {c.description || ''}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    )}
                                                    <Button size="small" startIcon={<AddIcon fontSize="small" />}
                                                        onClick={() => setAddCode({ open: true, desc: '' })}>
                                                        Add job code
                                                    </Button>
                                                </Box>
                                            )}
                                            {pendingForJob.length > 0 && (
                                                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#b45309' }}>
                                                    Pending: {[
                                                        ...pendingForJob.filter(p => p.code).map(p => `#${parentCode}-${p.code}`),
                                                        ...pendingNumbered.map((p, i) => `#${parentCode}-${lastCodeNo + i + 1} ${p.description}`),
                                                    ].join(', ')}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}

                                    <TextField
                                        multiline
                                        fullWidth
                                        placeholder={dictationSupported ? "Type or dictate notes..." : "Notes for this job..."}
                                        value={noteTexts[selectedJobId] || ''}
                                        onChange={(e) => handleJobNoteChange(selectedJobId, e.target.value)}
                                        autoFocus
                                        InputProps={{
                                            endAdornment: dictationSupported && (
                                                <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                                                    <IconButton
                                                        onClick={() => toggleDictation(selectedJobId)}
                                                        color={isListening && dictationTarget === selectedJobId ? 'error' : 'default'}
                                                        size="small"
                                                    >
                                                        {isListening && dictationTarget === selectedJobId ? <MicOffIcon /> : <MicIcon />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            flex: 1,
                                            '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' },
                                            '& textarea': { height: '100% !important', overflow: 'auto !important' },
                                        }}
                                    />
                                </>
                            ) : (
                                <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                                    Select a job to add notes
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} variant="contained">Close</Button>
            </DialogActions>
        </Dialog>
    );
}
